import {TRAILING_ZEROS_REGEX} from './constants';

type LocaleParts = {
  groupSeparator: string;
  decimalSeparator: string;
  minusSign: string;
};

const partsCache = new Map<string, LocaleParts>();

export const getParts = (locale: string): LocaleParts => {
  const cached = partsCache.get(locale);
  if (cached) {
    return cached;
  }

  const testFractionalFormat = Intl.NumberFormat(locale, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

  const parts = testFractionalFormat.formatToParts(123456.123);

  const group = parts
    .filter((part) => part.type === 'group')
    .map((part) => part.value);

  const decimal = parts
    .filter((part) => part.type === 'decimal')
    .map((part) => part.value);

  const negParts = Intl.NumberFormat(locale).formatToParts(-1);
  const minus = negParts
    .filter((part) => part.type === 'minusSign')
    .map((part) => part.value);

  const result: LocaleParts = {
    groupSeparator: group[0] ?? ',',
    decimalSeparator: decimal[0] ?? '.',
    minusSign: minus[0] ?? '-',
  };

  partsCache.set(locale, result);
  return result;
};

export const getDecimalSeparatorIndex = (
  formattedNumber: string,
  locale: string,
): number => {
  const {decimalSeparator} = getParts(locale);

  return formattedNumber.indexOf(decimalSeparator);
};

export function getFractionalPartInfo(
  formattedValue: string,
  locale: string,
): {
  decimalIndex: number;
  hasFractionalPart: boolean;
  firstTrailingZeroIndex: number;
} {
  const decimalIndex = getDecimalSeparatorIndex(formattedValue, locale);
  const hasFractionalPart = decimalIndex !== -1;
  const match = TRAILING_ZEROS_REGEX.exec(formattedValue);
  const firstTrailingZeroIndex = match ? match.index : Infinity;
  return {decimalIndex, hasFractionalPart, firstTrailingZeroIndex};
}

/**
 * Determines whether an ambiguous separator character (one that could be either
 * the group separator or decimal separator depending on locale) should be
 * treated as a decimal. This handles the iOS locale mismatch where the keyboard
 * shows a different separator than what the browser locale reports.
 *
 * A separator is treated as decimal when:
 * - It appears only once in the string
 * - What follows is only digits or nothing (incomplete input like "5,")
 * - It is NOT followed by exactly 3 digits (which is the canonical group pattern)
 * - fractionalDigits > 0 (the field allows decimals)
 */
function isAmbiguousSeparatorDecimal(
  value: string,
  separator: string,
  fractionalDigits: number,
): boolean {
  if (fractionalDigits === 0) {
    return false;
  }
  const sepRegex = new RegExp(separator.replace('.', '\\.'), 'g');
  const matches = value.match(sepRegex);
  if (!matches || matches.length !== 1) {
    return false;
  }
  const idx = value.indexOf(separator);
  const afterSep = value.slice(idx + separator.length);
  // Separator at end of string = incomplete decimal input (e.g. "5,")
  if (afterSep.length === 0) {
    return true;
  }
  // Exactly 3 trailing digits = canonical group pattern (e.g. "1,234").
  // This means 3-fractional-digit currencies (BHD, OMR) always resolve as
  // grouping in ambiguous cases — the safer default since X,XXX grouping is
  // overwhelmingly more common than 3-decimal input via the wrong separator.
  return /^\d+$/.test(afterSep) && afterSep.length !== 3;
}

export const parseFormattedNumber = (
  formattedNumber: string,
  locale: string,
  fractionalDigits: number,
  previousFormattedValue?: string,
  allowAmbiguousGroupSeparatorAsDecimal = true,
): number => {
  const {groupSeparator, decimalSeparator, minusSign} = getParts(locale);

  let isNegative = false;
  let stripped = formattedNumber;
  if (stripped.startsWith(minusSign)) {
    isNegative = true;
    stripped = stripped.slice(minusSign.length);
  } else if (minusSign !== '-' && stripped.startsWith('-')) {
    isNegative = true;
    stripped = stripped.slice(1);
  }

  const validCharsRegex = new RegExp(
    `^[0-9${groupSeparator.replace('.', '\\.')}${decimalSeparator.replace(
      '.',
      '\\.',
    )}]*$`,
  );

  if (!validCharsRegex.test(stripped)) {
    return NaN;
  }

  let numberWithoutLocale = stripped;

  // When group and decimal separators differ, check if the group separator is
  // actually being used as a decimal (iOS locale mismatch). Only apply when
  // the previous formatted value didn't already contain this separator as a
  // group separator — otherwise edits of grouped values (e.g. "1,234" → "1,23"
  // via backspace) would be misinterpreted.
  const previousHadGroupSeparator =
    previousFormattedValue != null &&
    previousFormattedValue.includes(groupSeparator);
  if (
    groupSeparator !== decimalSeparator &&
    !previousHadGroupSeparator &&
    allowAmbiguousGroupSeparatorAsDecimal &&
    isAmbiguousSeparatorDecimal(stripped, groupSeparator, fractionalDigits)
  ) {
    const groupRegExp = new RegExp(groupSeparator.replace('.', '\\.'), 'g');
    numberWithoutLocale = numberWithoutLocale.replace(groupRegExp, '.');
  } else {
    const groupRegExp = new RegExp(groupSeparator.replace('.', '\\.'), 'g');
    const decimalRegExp = new RegExp(decimalSeparator.replace('.', '\\.'), 'g');
    numberWithoutLocale = numberWithoutLocale
      .replace(groupRegExp, '')
      .replace(decimalRegExp, '.');
  }

  if (numberWithoutLocale === '.' || numberWithoutLocale === '') {
    if (!/\d/.test(stripped)) {
      return NaN;
    }
    return isNegative ? -0 : 0;
  }

  const [integerPart, fractionalPart = ''] = numberWithoutLocale.split('.');
  const truncatedFractional =
    fractionalDigits <= 0 ? '' : fractionalPart.slice(0, fractionalDigits);

  const parsedNumber = parseFloat(
    truncatedFractional.length
      ? `${integerPart ?? '0'}.${truncatedFractional}`
      : integerPart ?? '0',
  );

  return isNegative ? -parsedNumber : parsedNumber;
};

const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(
  locale: string,
  max: number,
  min: number,
): Intl.NumberFormat {
  const clampedMin = Math.min(min, max);
  const key = `${locale}|${max}|${clampedMin}`;
  let fmt = formatterCache.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(locale, {
      maximumFractionDigits: max,
      minimumFractionDigits: clampedMin,
    });
    formatterCache.set(key, fmt);
  }
  return fmt;
}

export const formatNumber = (
  parsedNumber: number,
  {
    fractionalDigits,
    minimumFractionDigits = fractionalDigits,
    locale,
    noDecimals = false,
    forcedZero = false,
  }: {
    fractionalDigits: number;
    minimumFractionDigits?: number;
    locale: string;
    noDecimals?: boolean;
    forcedZero?: boolean;
  },
) => {
  if (parsedNumber === 0 && !forcedZero) {
    return '';
  }

  const max = noDecimals ? 0 : fractionalDigits;
  const min = noDecimals ? 0 : minimumFractionDigits;
  return getFormatter(locale, max, min).format(parsedNumber);
};

export const getNextCursorPosition = (
  previousFormattedInput: string,
  currentFormattedInput: string,
  cursorPos: number,
  locale: string,
) => {
  if (previousFormattedInput.length === 0) {
    return cursorPos;
  }

  if (cursorPos === 0) {
    return 0;
  }

  const {groupSeparator, decimalSeparator, minusSign} = getParts(locale);

  if (cursorPos <= currentFormattedInput.length) {
    const prevSubstring = previousFormattedInput.substring(0, cursorPos);
    const currSubstring = currentFormattedInput.substring(0, cursorPos);
    if (prevSubstring === currSubstring) {
      return cursorPos;
    }
  }

  let digitsBeforeCursor = 0;
  let hasDecimalBeforeCursor = false;
  for (let i = 0; i < cursorPos && i < previousFormattedInput.length; i++) {
    const char = previousFormattedInput[i];
    if (char === decimalSeparator) {
      hasDecimalBeforeCursor = true;
    } else if (char !== groupSeparator && char !== minusSign && char !== '-') {
      digitsBeforeCursor++;
    }
  }

  let digitCount = 0;
  let foundDecimal = false;
  for (let i = 0; i < currentFormattedInput.length; i++) {
    const char = currentFormattedInput[i];

    if (char === decimalSeparator) {
      foundDecimal = true;
      if (hasDecimalBeforeCursor && digitCount === digitsBeforeCursor) {
        return i + 1;
      }
    } else if (char !== groupSeparator && char !== minusSign && char !== '-') {
      digitCount++;
      if (
        digitCount === digitsBeforeCursor &&
        (!hasDecimalBeforeCursor || foundDecimal)
      ) {
        return i + 1;
      }
    }
  }

  return currentFormattedInput.length;
};
